import * as L from 'leaflet';
import { TranslationService } from '../../../../services/translation.service';
import { ButtonDivIcon } from '../../../Shared/Map/markers/button-div-icon';
import { ICommuterParkingLot } from '../../../../models/commuter-parking-lot.model';
import { DateTime } from 'luxon';
import { SanitizerService } from '../../../../services/sanitizer.service';

export default class CommuterParkingLotClusterMarker extends L.Marker {
  private readonly createdDateTime: DateTime;

  constructor(commuterParkingLot: ICommuterParkingLot, createdDateTime: DateTime, onClick?: (clickedCommuterParkingLot: ICommuterParkingLot) => void | undefined, openPopup: boolean = false) {
    super([commuterParkingLot.latitude ?? 0, commuterParkingLot.longitude ?? 0], { zIndexOffset: 900 });
    this.createdDateTime = createdDateTime;

    this.setIcon(
      new ButtonDivIcon({
        className: 'button-div-icon commute-parking-lot-marker',
        iconSize: [34, 55],
        iconAnchor: [17, 55],
        popupAnchor: [0, -50],
        html: `<img src="/icons/leaflet/map-marker-commute-parking-lot-blue-1000.svg" alt="${TranslationService.get('common.parkingspaces')} ${SanitizerService.sanitizeHtml(
          commuterParkingLot.name
        )}" class="w-100 h-100" />`,
      })
    );

    this.bindPopup(this.createPopupHtml(commuterParkingLot), {
      autoPan: true,
      offset: [0, 0],
    });

    if (openPopup) {
      setTimeout(() => {
        this.openPopup();
      }, 200);
    }

    this.on('click', () => {
      if (onClick) onClick(commuterParkingLot);
    });

    this.on('popupopen', (popupEvent: L.PopupEvent) => {
      // Inspired by http://melmo.github.io/accessibility/code/dist/leaflet.html
      // Focus on the opened popup
      const stopAreaPopups = popupEvent.popup.getElement()?.getElementsByClassName('commute-parking-lot-popup') ?? [];
      if (stopAreaPopups.length > 0) {
        const stopAreaPopup = stopAreaPopups[0] as HTMLElement;
        stopAreaPopup.setAttribute('tabindex', '-1');
        stopAreaPopup.style.outline = 'none'; // Workaround for hiding built-in focus marker, see NV-4097
        stopAreaPopup.focus();
      }

      // Fix the a11y attributes on the close button
      const closeButtons = popupEvent.popup.getElement()?.getElementsByClassName('leaflet-popup-close-button') ?? [];
      if (closeButtons.length > 0) {
        const closeButton = closeButtons[0] as HTMLLinkElement;
        closeButton.setAttribute('role', 'button');
        closeButton.setAttribute('aria-label', TranslationService.get('common.map.closepopup'));
        closeButton.href = '#';
      }
    });
  }

  private createPopupHtml(commuterParkingLot: ICommuterParkingLot): string {
    return `<div class="commute-parking-lot-popup">
      <h3 class="commute-parking-lot-popup__name mb-2">${commuterParkingLot.name}</h3>
      <div>${TranslationService.get('stopareasearchpage.numberofparkingspots')}: ${commuterParkingLot.totalCapacity}</div>
      ${
        Number.isInteger(commuterParkingLot.freeSpaces)
          ? `<div>${TranslationService.get('stopareasearchpage.freeparkingspots')}: ${commuterParkingLot.freeSpaces}</div><small>${TranslationService.get(
              'stopareasearchpage.updated'
            )}: ${this.createdDateTime.toFormat('T')}</small>`
          : ''
      }
    </div>`;
  }
}
